// @ts-nocheck
/**
 * Diverging bar sparkline for Net Income across the file's periods.
 * Positive months render upward in emerald, negative downward in red,
 * with a hairline zero-axis through the middle. The point is to make
 * 71 cells of dollar-amount data legible at a glance: trend, gaps,
 * and which periods are losses become visible without scanning chips.
 *
 * Pure SVG — no charting dep. Width is responsive (uses viewBox), so
 * the parent controls the visible size via CSS.
 */
import React, { useMemo, useState } from 'react';

const POS = '#10b981';      // emerald-500
const POS_HOVER = '#059669';
const NEG = '#ef4444';      // red-500
const NEG_HOVER = '#dc2626';
const AXIS = 'rgba(15, 23, 42, 0.18)';

const fmtCompact = (n) => {
  if (n == null || Number.isNaN(n)) return '—';
  const abs = Math.abs(n);
  const sign = n < 0 ? '-' : '';
  if (abs >= 1_000_000) return `${sign}$${(abs / 1_000_000).toFixed(2)}M`;
  if (abs >= 1_000) return `${sign}$${(abs / 1_000).toFixed(1)}k`;
  return `${sign}$${abs.toFixed(0)}`;
};

const fmtFull = (n) => {
  if (n == null || Number.isNaN(n)) return '—';
  return n.toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });
};

// `series` is an array of { label, columnIndex, value } sorted in
// period order. `height` and the responsive width are set in CSS via
// the wrapping div; we just paint into a 1000×120 viewBox.
const NetIncomeSparkline = ({ series, periods }) => {
  const [hover, setHover] = useState(null); // { i, x, y, value, label }

  const ordered = useMemo(() => {
    if (!series?.length) return [];
    // The agent emits value_by_period keyed by column index. Map each
    // entry back to its period label so the tooltip reads naturally.
    const labelByCol = new Map();
    (periods || []).forEach((p) => {
      labelByCol.set(String(p.column_index), p.display_name);
    });
    return series.map((s, i) => ({
      i,
      columnIndex: s.columnIndex,
      value: s.value,
      label: labelByCol.get(String(s.columnIndex)) || `col ${s.columnIndex}`,
    }));
  }, [series, periods]);

  if (!ordered.length) {
    return <div className="spark-empty">No per-period values.</div>;
  }

  const W = 1000;
  const H = 120;
  const PAD_X = 4;
  const PAD_Y = 8;
  const innerW = W - PAD_X * 2;
  const innerH = H - PAD_Y * 2;

  const max = Math.max(0, ...ordered.map((d) => d.value || 0));
  const min = Math.min(0, ...ordered.map((d) => d.value || 0));
  const range = Math.max(Math.abs(max), Math.abs(min)) || 1;

  // Zero-line sits at the vertical midpoint.
  const zeroY = PAD_Y + innerH / 2;
  const halfH = innerH / 2;

  const n = ordered.length;
  // Each bar is allocated a slot; we leave 30% empty between bars.
  const slotW = innerW / n;
  const barW = Math.max(1.2, slotW * 0.72);

  const positives = ordered.filter((d) => d.value > 0).length;
  const negatives = ordered.filter((d) => d.value < 0).length;

  return (
    <div className="spark-wrap">
      <svg className="spark-svg" viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none">
        {/* Zero axis */}
        <line x1={0} x2={W} y1={zeroY} y2={zeroY} stroke={AXIS} strokeWidth="1" shapeRendering="crispEdges" />

        {ordered.map((d) => {
          const cx = PAD_X + slotW * d.i + slotW / 2;
          const x = cx - barW / 2;
          const v = d.value || 0;
          const h = (Math.abs(v) / range) * halfH;
          const isPos = v >= 0;
          const y = isPos ? zeroY - h : zeroY;
          const isHover = hover?.i === d.i;
          const fill = isPos ? (isHover ? POS_HOVER : POS) : (isHover ? NEG_HOVER : NEG);
          return (
            <rect
              key={d.i}
              x={x}
              y={y}
              width={barW}
              height={Math.max(0.6, h)}
              fill={fill}
              rx={Math.min(1.5, barW / 3)}
              onMouseEnter={() => setHover({ ...d, x: cx, y })}
              onMouseLeave={() => setHover(null)}
            />
          );
        })}
      </svg>

      {/* Bottom legend */}
      <div className="spark-legend">
        <span className="spark-legend-item">
          <span className="spark-swatch" style={{ background: POS }} />
          {positives} positive
        </span>
        <span className="spark-legend-item">
          <span className="spark-swatch" style={{ background: NEG }} />
          {negatives} negative
        </span>
      </div>

      {hover && (
        <div
          className="spark-tooltip"
          style={{ left: `${(hover.x / W) * 100}%` }}
        >
          <div className="spark-tooltip-label">{hover.label}</div>
          <div className={`spark-tooltip-value ${hover.value < 0 ? 'neg' : 'pos'}`}>
            {fmtFull(hover.value)}
          </div>
          <div className="spark-tooltip-col">col {hover.columnIndex}</div>
        </div>
      )}
    </div>
  );
};

export default NetIncomeSparkline;
export { fmtCompact, fmtFull };
